enum LegalEntity {
  LLP
  LIMITED
  PLC
  SOLE_TRADER
  PARTNERSHIP
  DIVISION
  GOVERNMENTAL
}

enum FeeType {
  PREADVICE_FIXED
  PRERECOMMENDATION_FIXED
  PRERECOMMENDATION_PERCENT
  PREAPPLICATION_FIXED
  PREAPPLICATION_PERCENT
  POSTAPPLICATION_FIXED
  POSTAPPLICATION_PERCENT
  OFFER_FIXED
  OFFER_PERCENT
  LEGAL_COMPLETION_FIXED
  LEGAL_COMPLETION_PERCENT
  LEGAL_EXCHANGE_FIXED
  LEGAL_EXCHANGE_PERCENT
  INVALID_FEE_TYPE
}

enum PanelType {
  INVALID_LENDER_PANEL_TYPE
  COMPREHENSIVE
  WHOLE_EXC_DIRECT
  WHOLE_INC_DIRECT
  NO_MORTGAGE_ADVICE
  LIMITED
  REFER
  WHOLE
  ONE
  NONE
}

enum VerificationClassType {
  INVALID_ENFORCEMENT_LEVEL
  ENFORCED
  OPTIONAL
  DISABLED
}

enum TemplateAssigneeType {
  INVALID
  USER
  GROUP
  CREATOR
}

enum OrganisationType {
  INTRODUCER
  DA
  INSURANCE
  CLUB
  ACRE
  AR
  LENDER
  INVALID_ORGANISATION_TYPE
}

enum ProtectionSettingsDecreasingTermPolicyRateDefaultType {
  INVALID
  FIXED
  MORTGAGE_RATE_PLUS
}

type OrganisationBankAccount implements BankAccount {
  sort_code: String
  number: String
}

type Fee {
  type: FeeType
  value: String
}

type FeeOption {
  fee_option_title: String
  fees: [NewFee!]
  default: Boolean
}

type VerificationClass {
  enhanced: VerificationClassType
  optional: VerificationClassType
  beta: VerificationClassType
}

type DefaultAssignee {
  type: TemplateAssigneeType
  id: String
}

type NoteTemplate {
  id: ID!
  title: String
  body: String
  disabled: Boolean
  note_type: NoteType
  case_types: [MortgageReason]
  default_assignees: [DefaultAssignee]
  default_delay_minutes: Int
  default_priority: Int
}

type Period {
  id: ID
  name: String
  start_date: String
  end_date: String
  disabled: Boolean
}

type PeriodsSettings {
  periods: [Period!]
}

type ClientPortalSettings {
  primary_colour: String
}

type AccountingDetails {
  vat_registered: Boolean
  vat_number: String
  bank_details_sort_code: String
  bank_details_account_no: String
}

type Organisation {
  id: ID!
  organisation_id: ID
  external_id: ID
  name: String
  website: String
  status: Status
  organisation_legal_form: LegalEntity
  fca_reg_no: String
  joined_acre: String
  first_time_access: FirstTimeAccess
  client_portal_url: String
  representative_of_org(useClientApi: Boolean): Organisation
  representative_of: String
  arOrganisationsMi(include_disabled_users: Boolean, bookmark: Int, page_size: Int): MiOrganisationResponse
  administered_by: Organisation
  paid_by: Organisation
  primary_address: LutherAddress
  primary_phone: String
  complaints_address: LutherAddress
  complaints_phone: String
  complaints_email: String
  compliance_email: String
  complaints_url: String
  bank_account: OrganisationBankAccount
  users: [User!]
  clients: [ClientVersion!]
  templates: [Template!]
  fees: [NewFee!]
  metadata: [Metadata!]
  standard_fee_options: [FeeOption!]
  general_email: String
  panel_management: Panel
  verification_class: VerificationClass
  note_templates: [NoteTemplate]
  periods_settings: PeriodsSettings
  logo: String
  client_portal_settings: ClientPortalSettings
  type: OrganisationType
  commission_structure(introducerId: ID!): CommissionStructure
  process_settings: ProcessSettings
  lender_metadata: LenderMetadata
  organisation_ext_identifier: String
  rmar_settings: RmarSettings
  sourcing_settings: SourcingSettings
  protection_settings: OrganisationProtectionSettings
  referral_settings: ReferralSettings
  bank_details_account_no: String
  bank_details_sort_code: String
  accounting: AccountingDetails
}

type ReferralSettings {
  excluded_referral_partners: [ExcludedReferralPartner]
}

type ExcludedReferralPartner {
  id: ID!
  organisation_id: String!
}

type RmarSettings {
  threshold_conditions: [ThresholdConditions]
  appointed_representatives_monitoring: [AppointedRepresentativesMonitoring]
}

type ThresholdConditions {
  id: ID
  close_links: CloseLinksAndControllerType
  controller: CloseLinksAndControllerType
  start_date: String
  end_date: String
}

type AppointedRepresentativesMonitoring {
  id: ID
  start_date: String
  end_date: String
  subject_to_monitoring: Int
  subject_to_financial_checks: Int
  has_other_monitoring_occurred: Boolean
}

type CloseLinksAndControllerType {
  notifiable_change_occurred: Boolean
  fca_notified: Boolean
}

type TemplateStyles {
  protection_advice_disclaimer: TemplateStyleVariant
}

type ProcessSettings {
  use_exchange_case_status: Boolean
  case_board: [CaseBoard]
  protection_disclaimer: ProtectionDisclaimer
  template_styles: TemplateStyles
  risk_flagging: RiskFlagging
  nationality_risk_list: String
}

type RiskFlagging {
  right_to_buy: String
  vulnerable: String
  second_job: String
  gifted_deposit: String
  lending_into_retirement: String
  redemption_penalty: String
  residential_interest_only: String
  equity_release: String
  debt_consolidation: String
  high_risk_debt_consolidation: String
  high_commission: String
  high_client_fees: String
  cheapest_product_not_recommended: String
  advisor_under_review: String
}

type ProtectionDisclaimer {
  generated_by: String
  signed_by: String
}

type LenderMetadata {
  porting_pay_on_full_loan_amount: Boolean
}

type CaseBoard {
  name: String
  statuses: [CaseStatus]
}

type OrganisationResponse {
  organisations: [Organisation!]
  next_page: GetOrganisationRequest
}

type MiOrganisationResponse {
  organisations: [MiOrganisation!]
  next_page: MiOrganisationNextPage
  filters: MiOrganisationFilters
}

type MiOrganisationNextPage {
  bookmark: Int
}

type MiOrganisationFilters {
  all_users: Boolean
  bookmark: Int
  include_disabled_orgs: Boolean
  include_disabled_users: Boolean
  org_id: [ID!]
  page_size: Int
  primary_role_ids: [ID]
  representative_of: String
}

type MiOrganisation {
  general_email: String
  organisation_id: ID!
  name: String
  primary_phone: String
  primary_postcode: String
  primary_posttown: String
  users: [MiUser!]
}

type MiUser {
  user_id: ID
  first_name: String
  last_name: String
  primary_role_id: ID
}

type GetOrganisationRequest {
  organisation_details: Boolean
  include_financial_configuration_fields: Boolean
  organisation_ids: [ID]
  organisation_ext_identifiers: [String]
  filter_representative_of_org_id: String
  filter_administered_by_org_id: String
  filter_regulated_by_org_id: String
  filter_paid_by_org_id: String
  bookmark: String
  page_size: Int
}

type SourcingSettings {
  true_cost_add_fees_to_loan_default: Boolean
  true_cost_add_fees_to_loan_locked: Boolean
  true_cost_add_permitted_fees_default: Boolean
  true_cost_add_permitted_fees_locked: Boolean
  true_cost_add_broker_fees_default: Boolean
  true_cost_add_broker_fees_locked: Boolean
  true_cost_add_redemption_fees_default: Boolean
  true_cost_add_redemption_fees_locked: Boolean
  true_cost_add_upfront_fees_default: Boolean
  true_cost_add_upfront_fees_locked: Boolean
  true_cost_add_erc_default: Boolean
  true_cost_add_erc_locked: Boolean
  true_cost_add_hlc_default: Boolean
  true_cost_add_hlc_locked: Boolean
  true_cost_exclude_cashback_default: Boolean
  true_cost_exclude_cashback_locked: Boolean
  true_cost_exclude_legal_fees_default: Boolean
  true_cost_exclude_legal_fees_locked: Boolean
  true_cost_include_racp_default: Boolean
  true_cost_include_racp_locked: Boolean
  true_cost_add_broker_fee_to_loan_default: Boolean
  true_cost_add_broker_fee_to_loan_locked: Boolean
  require_true_cost_calculation_change_reason: Boolean
}

type ProtectionSettingsCommission {
  indemnity_required: Boolean
}

type OrganisationProtectionSettings {
  decreasing_term_policy_rate_default_type: ProtectionSettingsDecreasingTermPolicyRateDefaultType
  decreasing_term_policy_rate_default_rate: Int
  commission: ProtectionSettingsCommission
}

input FeeInput {
  fee_type: FeeType
  value: String
}

input FeeOptionInput {
  fee_option_title: String
  fees: [FeeInput]
  default: Boolean
}

type Metadata {
  name: String
  string_value: String
  bool_value: Boolean
  user_editable: Boolean
}

input MetadataInput {
  name: String
  string_value: String
  bool_value: Boolean
  user_editable: Boolean
}

input DefaultAssigneeInput {
  type: TemplateAssigneeType
  id: String
}

input NoteTemplateInput {
  id: ID
  title: String
  body: String
  disabled: Boolean
  note_type: NoteType
  case_types: [MortgageReason]
  default_assignees: [DefaultAssigneeInput]
  default_delay_minutes: Int
  default_priority: Int
}

input OrganisationInput {
  id: ID
  external_id: ID
  name: String
  logo: String
  primary_address: LutherAddressInput
  complaints_address: LutherAddressInput
  primary_phone: String
  website: String
  general_email: String
  first_time_access: FirstTimeAccessInput
  metadata: [MetadataInput!]
  fees: [FeeInput!]
  standard_fee_options: [FeeOptionInput!]
  type: OrganisationType
  organisation_legal_form: LegalEntity
  fca_reg_no: String
  panel_management: PanelInput
  verification_class: VerificationClassInput
  note_templates: [NoteTemplateInput]
  representative_of: String
  periods_settings: PeriodsSettingsInput
  client_portal_url: String
  complaints_url: String
  complaints_email: String
  compliance_email: String
  complaints_phone: String
  status: Status
  client_portal_settings: ClientPortalSettingsInput
  process_settings: ProcessSettingsInput
  organisation_ext_identifier: String
  rmar_settings: RmarSettingsInput
  sourcing_settings: SourcingSettingsInput
  protection_settings: OrganisationProtectionSettingsInput
  referral_settings: ReferralSettingsInput
  accounting: AccountingDetailsInput
  paid_by: String
}

input ReferralSettingsInput {
  excluded_referral_partners: [ExcludedReferralPartnerInput]
}

input ExcludedReferralPartnerInput {
  id: ID
  organisation_id: String!
}

input RmarSettingsInput {
  threshold_conditions: [ThresholdConditionsInput]
  appointed_representatives_monitoring: [AppointedRepresentativesMonitoringInput]
}

input AccountingDetailsInput {
  vat_registered: Boolean
  vat_number: String
  bank_details_sort_code: String
  bank_details_account_no: String
}

input SourcingSettingsInput {
  true_cost_add_fees_to_loan_default: Boolean
  true_cost_add_fees_to_loan_locked: Boolean
  true_cost_add_permitted_fees_default: Boolean
  true_cost_add_permitted_fees_locked: Boolean
  true_cost_add_broker_fees_default: Boolean
  true_cost_add_broker_fees_locked: Boolean
  true_cost_add_redemption_fees_default: Boolean
  true_cost_add_redemption_fees_locked: Boolean
  true_cost_add_upfront_fees_default: Boolean
  true_cost_add_upfront_fees_locked: Boolean
  true_cost_add_erc_default: Boolean
  true_cost_add_erc_locked: Boolean
  true_cost_add_hlc_default: Boolean
  true_cost_add_hlc_locked: Boolean
  true_cost_exclude_cashback_default: Boolean
  true_cost_exclude_cashback_locked: Boolean
  true_cost_exclude_legal_fees_default: Boolean
  true_cost_exclude_legal_fees_locked: Boolean
  true_cost_include_racp_default: Boolean
  true_cost_include_racp_locked: Boolean
  true_cost_add_broker_fee_to_loan_default: Boolean
  true_cost_add_broker_fee_to_loan_locked: Boolean
  require_true_cost_calculation_change_reason: Boolean
}

input ProtectionSettingsCommissionInput {
  indemnity_required: Boolean
}

input OrganisationProtectionSettingsInput {
  decreasing_term_policy_rate_default_type: ProtectionSettingsDecreasingTermPolicyRateDefaultType
  decreasing_term_policy_rate_default_rate: Int
  commission: ProtectionSettingsCommissionInput
}

input ThresholdConditionsInput {
  id: ID
  close_links: CloseLinksAndControllerInput
  controller: CloseLinksAndControllerInput
  start_date: String
  end_date: String
}

input CloseLinksAndControllerInput {
  notifiable_change_occurred: Boolean
  fca_notified: Boolean
}

input AppointedRepresentativesMonitoringInput {
  id: ID
  start_date: String
  end_date: String
  subject_to_monitoring: Int
  subject_to_financial_checks: Int
  has_other_monitoring_occurred: Boolean
}

input TemplateStylesInput {
  protection_advice_disclaimer: TemplateStyleVariant
}

input ProcessSettingsInput {
  use_exchange_case_status: Boolean
  case_board: [CaseBoardInput]
  protection_disclaimer: ProtectionDisclaimerInput
  template_styles: TemplateStylesInput
  risk_flagging: RiskFlaggingInput
  nationality_risk_list: String
}

input RiskFlaggingInput {
  right_to_buy: String
  vulnerable: String
  second_job: String
  gifted_deposit: String
  lending_into_retirement: String
  redemption_penalty: String
  residential_interest_only: String
  equity_release: String
  debt_consolidation: String
  high_risk_debt_consolidation: String
  high_commission: String
  high_client_fees: String
  cheapest_product_not_recommended: String
  advisor_under_review: String
}

input ProtectionDisclaimerInput {
  generated_by: String
  signed_by: String
}

input CaseBoardInput {
  name: String
  statuses: [CaseStatus]
}

input PeriodsSettingsInput {
  periods: [PeriodInput]
}

input PeriodInput {
  id: ID
  name: String
  start_date: String
  end_date: String
  disabled: Boolean
}

input ClientPortalSettingsInput {
  primary_colour: String
}

input SetupOrganisationInput {
  organisation: OrganisationInput!
  user: UserInput!
}

input DefaultAssigneesInput {
  type: String
  id: ID
}

type MiOrganisationSummary {
  case_count: Int
  general_email: String
  primary_phone: String
  joined_acre: String
  metadata: [Metadata!]
  mortage_volume: String
  name: String!
  organisation_id: ID!
  primary_postcode: String!
  primary_posttown: String!
  principal_ids: [String!]
  type: OrganisationType
  user_count: Int
}

type MiOrganisationSummaryNextPage {
  bookmark: Int
}

input MiOrganisationInput {
  all_users: Boolean
  bookmark: Int
  include_disabled_orgs: Boolean
  include_disabled_users: Boolean
  org_ids: [String!]
  page_size: Int
  representative_of: String
  primary_role_ids: [String!]
}

input MiOrganisationsInput {
  bookmark: Int
  start_date: String
  end_date: String
  general_emails_exclude: [String!]
  metadata: Boolean
  org_ids: [String!]
  org_ids_exclude: [String!]
  org_types: [OrganisationType]
  page_size: Int
}

input GenerationInput {
  fee_type: CommissionRevenueType
  payment_due: PaymentDue
}

input FeeConfigurationInput {
  club_id: String
  generations: [GenerationInput]
  rebate_payment_schedule: String
  rebate_reference_date: String
  gross_net_split: String
  rebate: Float
}

input EquityReleaseLifetimeInput {
  lifetime_advice: Boolean
  type: PanelType
  providers: [String!]
}

input PanelProtectionInput {
  protection_advice: Boolean
  refer_specialist: Boolean
  business_protection_advice: Boolean
  family_income_advice: Boolean
  whole_of_life_advice: Boolean
  type: PanelType
  providers: [String!]
  unadvised: Boolean
}

input PMIInput {
  pmi_advice: Boolean
  type: PanelType
  providers: [String!]
}

input ASUInput {
  asu_advice: Boolean
  type: PanelType
  providers: [String!]
}

input GIInput {
  gi_advice: Boolean
  type: PanelType
  uinsure_auto_refer: Boolean
  paymentshield_branch_number: String
  providers: [String!]
}

input PanelInput {
  panel: PanelType
  excluded_lenders: [String]
  preferred_clubs: [String]
  default_club: String
  mortgage_advice: Boolean
  second_charge_advice: Boolean
  bridging_advice: Boolean
  commercial_advice: Boolean
  equity_release_lifetime: EquityReleaseLifetimeInput
  protection: PanelProtectionInput
  pmi: PMIInput
  asu: ASUInput
  gi: GIInput
  fee_configurations: [FeeConfigurationInput]
}

input VerificationClassInput {
  enhanced: VerificationClassType
  optional: VerificationClassType
  beta: VerificationClassType
}

type MiOrganisationsFilter {
  bookmark: Int
  start_date: String
  end_date: String
  general_emails_exclude: [String!]
  metadata: Boolean
  org_ids: [String!]
  org_ids_exclude: [String!]
  types: [OrganisationType]
  page_size: Int
}

type MiOrganisationsResponse {
  filters: MiOrganisationsFilter!
  next_page: MiOrganisationSummaryNextPage
  organisations: [MiOrganisationSummary!]!
}

type Generation {
  fee_type: CommissionRevenueType
  payment_due: PaymentDue
}

type FeeConfiguration {
  club_id: String
  generations: [Generation]
  rebate_payment_schedule: String
  rebate_reference_date: String
  gross_net_split: String
  rebate: Float
}

type EquityReleaseLifetime {
  lifetime_advice: Boolean
  type: PanelType
  providers: [String!]
}

type PanelProtection {
  protection_advice: Boolean
  refer_specialist: Boolean
  business_protection_advice: Boolean
  family_income_advice: Boolean
  whole_of_life_advice: Boolean
  type: PanelType
  providers: [String!]
  unadvised: Boolean
}

type PMI {
  pmi_advice: Boolean
  type: PanelType
  providers: [String!]
}

type ASU {
  asu_advice: Boolean
  type: PanelType
  providers: [String!]
}

type GI {
  gi_advice: Boolean
  type: PanelType
  uinsure_auto_refer: Boolean
  paymentshield_branch_number: String
  providers: [String!]
}

type Panel {
  panel: PanelType
  excluded_lenders: [String!]
  preferred_clubs: [String!]
  default_club: String
  mortgage_advice: Boolean
  second_charge_advice: Boolean
  bridging_advice: Boolean
  commercial_advice: Boolean
  equity_release_lifetime: EquityReleaseLifetime
  protection: PanelProtection
  pmi: PMI
  asu: ASU
  gi: GI
  fee_configurations: [FeeConfiguration]
}
